
import React from 'react';
import styles from './_lease-vehicle-sale-editor.module.scss';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import {hasValue, toDollars, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import {IApplication} from "../../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../../Interfaces/Application/IApplicationQuote";
import {IConfiguration} from "../../../../../Data/ConfigurationData";
import {
  getLeaseQuoteErrorMessages
} from "../../../../../utils/quoteUtils";
import QuoterError from "../../../QuoterError/QuoterError";
import LeaseQuoterSolveButton from "../../LeaseQuoterSolveButton/LeaseQuoterSolveButton";
import {ILeaseLoanClass} from "../../../../../Interfaces/Common/ILeaseLoanClass";

export interface LeaseVehicleSaleEditorProps {
  onChangeSingleField: (fieldName: string, value: number | Date | string | null | undefined) => void;
  currentApp: IApplication;
  applicationLeaseErrors: IRuleViolation[];
  configuration: IConfiguration;
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  selectedLeaseLoanClass: ILeaseLoanClass | undefined;
}

export default function LeaseVehicleSaleEditor (props:  LeaseVehicleSaleEditorProps) {
  const applicationLeaseQuote = props.currentApp.applicationLeaseQuote;
  const errorMessages = getLeaseQuoteErrorMessages(props.applicationLeaseErrors);
  
  const totalDeferredDown = (applicationLeaseQuote.deferredPmtAmount1 ?? 0)
    + (applicationLeaseQuote.deferredPmtAmount2 ?? 0)
    + (applicationLeaseQuote.deferredPmtAmount3 ?? 0)
    + (applicationLeaseQuote.deferredPmtAmount4 ?? 0)
    + (applicationLeaseQuote.deferredPmtAmount5 ?? 0)
    + (applicationLeaseQuote.deferredPmtAmount6 ?? 0);

  const totalFees = applicationLeaseQuote.gapFee
  + applicationLeaseQuote.docFee
  + applicationLeaseQuote.titleFee
  + applicationLeaseQuote.licenseFee
  + applicationLeaseQuote.batteryFee
  + applicationLeaseQuote.transferFee
  + applicationLeaseQuote.deliveryFee
  + applicationLeaseQuote.serviceWarranty
  + applicationLeaseQuote.dispositionFee
  + applicationLeaseQuote.acquisitionFee
  + applicationLeaseQuote.customFee1
  + applicationLeaseQuote.customFee2
  + applicationLeaseQuote.customFee3;
  
  return (
    <div className={styles.leaseVehicleSaleEditorContainer}>
      <div className={styles.sectionItem}>
        <DatePickerWithYearSanitizer
          disabled={props.leaseQuoteFieldIsDisabled("contractDate")}
          valid={!hasValue(errorMessages.contractDate)}
          label={'Contract Date'}
          value={new Date(applicationLeaseQuote.contractDate!)}
          onChange={(event: DatePickerChangeEvent) => props.onChangeSingleField('contractDate', event.value)}
        />
        <QuoterError message={errorMessages.contractDate}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("residualPercent")}
          valid={!hasValue(errorMessages.residualPercent)}
          value={applicationLeaseQuote.residualPercent}
          label={'Residual Percent'}
          type='number'
          step={1}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('residualPercent', Number(e.target.value))}
          style={{width: "100%"}}
        />
        <QuoterError message={errorMessages.residualPercent}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("sellingPrice")}
          valid={!hasValue(errorMessages.sellingPrice)}
          label={'Selling Price'}
          value={applicationLeaseQuote.sellingPrice}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('sellingPrice', Number(e.target.value))}
          type='number'
        />
        <QuoterError message={errorMessages.sellingPrice}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("cashDown")}
          valid={!hasValue(errorMessages.cashDown)}
          label={'Cash Down'}
          value={applicationLeaseQuote.cashDown}
          type='number'
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('cashDown', Number(e.target.value))}
          step={100}
        />
        <QuoterError message={errorMessages.cashDown}/>
      </div>
      <div className={styles.sectionItem}>
        <div className={styles.rollBack}>
          <Input
            readOnly={props.leaseQuoteFieldIsDisabled("totalDownRollback")}
            valid={!hasValue(errorMessages.totalDownRollback)}
            value={applicationLeaseQuote.totalDownRollback}
            label={'Total Down Roll Back'}
            type='number'
            step={100}
            onChange={(e: InputChangeEvent) => props.onChangeSingleField('totalDownRollback', Number(e.target.value))}
            style={{width: "100%"}}
          />
          <div style={{marginTop: "10px"}}>
            <LeaseQuoterSolveButton
              {...props}
              buttonName={"Roll Back"}
            />
          </div>
        </div>
        <QuoterError message={errorMessages.totalDownRollback}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("securityDeposit")}
          valid={!hasValue(errorMessages.securityDeposit)}
          value={applicationLeaseQuote.securityDeposit}
          label={'Security Deposit'}
          type='number'
          step={1}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('securityDeposit', Number(e.target.value))}
          style={{width: "100%"}}
        />
        <QuoterError message={errorMessages.securityDeposit}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("mileage")}
          valid={!hasValue(errorMessages.mileage)}
          value={applicationLeaseQuote.mileage}
          label={'Mileage'}
          type='number'
          step={1000}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('mileage', Number(e.target.value))}
          style={{width: "100%"}}
        />
        <QuoterError message={errorMessages.mileage}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("discountPercent")}
          valid={!hasValue(errorMessages.discountPercent)}
          value={applicationLeaseQuote.discountPercent}
          label={'Discount %'}
          type='number'
          step={1}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('discountPercent', Number(e.target.value))}
          style={{width: "100%"}}
        />
        <QuoterError message={errorMessages.discountPercent}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalDeferredDown)}
          label={'Total Deferred Down'}
          value={toDollars(totalDeferredDown)}
          type='text'
        />
        <QuoterError message={errorMessages.totalDeferredDown}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalFees)}
          label={'Total Fees'}
          value={toDollars(totalFees)}
          type='text'
        />
        <QuoterError message={errorMessages.totalFees}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalTaxes)}
          label={'Total Taxes'}
          type='text'
          value={toDollars(applicationLeaseQuote.totalTaxes)}
        />
        <QuoterError message={errorMessages.totalTaxes}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly={props.leaseQuoteFieldIsDisabled("tradeAllowance")}
          valid={!hasValue(errorMessages.tradeAllowance)}
          label={'Trade Allowance'}
          value={applicationLeaseQuote.tradeAllowance}
          onChange={(e: InputChangeEvent) => props.onChangeSingleField('tradeAllowance', Number(e.target.value))}
          type='number'
        />
        <QuoterError message={errorMessages.tradeAllowance}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.tradeInLien)}
          label={'Trade In Lien'}
          value={toDollars(props.currentApp.tradeInVehicle?.tradeBalance ?? 0)}
          type='text'
        />
        <QuoterError message={errorMessages.tradeInLien}/>
      </div>
    </div>
  );
}
